import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";
import dayjs from "dayjs";

// Config-driven filter field. The consumer declares *what* a field is (type +
// labels/options/value/onChange); this component owns *how* it renders. It holds
// no business logic (URL params, data fetching, domain rules) and no layout
// (Grid sizing) — both stay with the consumer. Drives a whole filter panel from
// a config array.
export type FilterFieldConfig<T = unknown> =
  | {
      type: "checkbox";
      label: string;
      value: boolean;
      onChange: (value: boolean) => void;
    }
  | {
      type: "switch";
      label: string;
      value: boolean;
      onChange: (value: boolean) => void;
    }
  | {
      type: "text";
      label?: string;
      placeholder?: string;
      value: string;
      onChange: (value: string) => void;
      constraint?: { maxLength?: number };
    }
  | {
      type: "toggle";
      title?: string;
      options: { value: string; label: string }[];
      value: string;
      onChange: (value: string) => void;
      ariaLabel?: string;
    }
  | {
      type: "select";
      label: string;
      options: T[];
      value: T | null;
      onChange: (value: T | null) => void;
      getOptionLabel: (option: T) => string;
      groupBy?: (option: T) => string;
      highlightMatch?: boolean;
      isOptionEqualToValue?: (option: T, value: T) => boolean;
      id?: string;
    }
  | {
      type: "multiselect";
      label: string;
      options: T[];
      value: T[];
      onChange: (value: T[]) => void;
      getOptionLabel: (option: T) => string;
      isOptionEqualToValue?: (option: T, value: T) => boolean;
      groupBy?: (option: T) => string;
      id?: string;
      // When true, render a "select all" checkbox row at the top of the
      // dropdown. It reflects all/none/indeterminate and toggles every option.
      enableSelectAll?: boolean;
      selectAllLabel?: string;
      deselectAllLabel?: string;
    }
  | {
      type: "date-range";
      labelFrom: string;
      labelTo: string;
      startDate: string;
      endDate: string;
      onStartChange: (value: string) => void;
      onEndChange: (value: string) => void;
    };

export function FilterField<T>(config: FilterFieldConfig<T>) {
  switch (config.type) {
    case "checkbox":
      return (
        <FormControlLabel
          control={
            <Checkbox
              checked={config.value}
              onChange={(event) => config.onChange(event.target.checked)}
            />
          }
          label={config.label}
          labelPlacement="end"
        />
      );

    case "switch":
      return (
        <FormControlLabel
          control={
            <Switch
              checked={config.value}
              onChange={(event) => config.onChange(event.target.checked)}
            />
          }
          label={config.label}
          labelPlacement="end"
        />
      );

    case "text":
      return (
        <TextField
          variant="outlined"
          fullWidth
          label={config.label}
          placeholder={config.placeholder}
          value={config.value}
          onChange={(event) => config.onChange(event.target.value)}
          inputProps={{ maxLength: config.constraint?.maxLength }}
        />
      );

    case "toggle":
      return (
        <>
          {config.title && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="subtitle2">{config.title}</Typography>
            </Box>
          )}
          <ToggleButtonGroup
            value={config.value}
            exclusive
            aria-label={config.ariaLabel}
            onChange={(_event, newValue) => {
              if (newValue !== null) {
                config.onChange(newValue);
              }
            }}
          >
            {config.options.map((option) => (
              <ToggleButton key={option.value} value={option.value}>
                {option.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </>
      );

    case "select": {
      const { getOptionLabel, highlightMatch } = config;
      return (
        <Autocomplete
          id={config.id}
          options={config.options}
          value={config.value}
          onChange={(_event, newValue) => config.onChange(newValue)}
          getOptionLabel={getOptionLabel}
          groupBy={config.groupBy}
          isOptionEqualToValue={config.isOptionEqualToValue}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              label={config.label}
              fullWidth
            />
          )}
          renderOption={
            highlightMatch
              ? (props, option, { inputValue }) => {
                  const label = getOptionLabel(option);
                  const matches = match(label, inputValue, {
                    insideWords: true,
                  });
                  const parts = parse(label, matches);

                  return (
                    <li {...props}>
                      {parts.map((part, index) => (
                        <span
                          key={index}
                          style={{ fontWeight: part.highlight ? 700 : 400 }}
                        >
                          {part.text}
                        </span>
                      ))}
                    </li>
                  );
                }
              : undefined
          }
        />
      );
    }

    case "multiselect": {
      const { options, value, onChange, getOptionLabel, enableSelectAll } =
        config;
      const allSelected = options.length > 0 && value.length === options.length;
      const someSelected = value.length > 0 && !allSelected;

      return (
        <Autocomplete
          multiple
          disableCloseOnSelect
          id={config.id}
          options={options}
          value={value}
          onChange={(_event, newValue) => onChange(newValue)}
          getOptionLabel={getOptionLabel}
          groupBy={config.groupBy}
          isOptionEqualToValue={config.isOptionEqualToValue}
          renderTags={(tagValue, getTagProps) => {
            // Cap visible tags so a large selection can't blow up the input
            // height; collapse the rest into a hover-to-reveal "+N" chip.
            const limit = 7;
            const shown = tagValue.slice(0, limit);
            const hidden = tagValue.slice(limit);
            return (
              <>
                {shown.map((option, index) => {
                  const { key, ...tagProps } = getTagProps({ index });
                  return (
                    <Chip
                      key={key}
                      label={getOptionLabel(option)}
                      {...tagProps}
                    />
                  );
                })}
                {hidden.length > 0 && (
                  <Tooltip title={hidden.map(getOptionLabel).join(", ")}>
                    <Chip label={`+${hidden.length}`} />
                  </Tooltip>
                )}
              </>
            );
          }}
          renderOption={
            enableSelectAll
              ? (props, option, { selected }) => {
                  const { key, ...optionProps } = props as typeof props & {
                    key: string;
                  };
                  return (
                    <li key={key} {...optionProps}>
                      <Checkbox sx={{ mr: 1 }} checked={selected} />
                      {getOptionLabel(option)}
                    </li>
                  );
                }
              : undefined
          }
          PaperComponent={
            enableSelectAll
              ? ({ children, ...paperProps }) => (
                  <Paper {...paperProps}>
                    {/* Sticky select-all row above the (optionally grouped)
                        option list. preventDefault keeps the input focused so
                        the popup stays open on click. */}
                    <MenuItem
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => onChange(allSelected ? [] : [...options])}
                    >
                      <Checkbox
                        sx={{ mr: 1 }}
                        checked={allSelected}
                        indeterminate={someSelected}
                      />
                      {allSelected
                        ? (config.deselectAllLabel ?? "Deselect all")
                        : (config.selectAllLabel ?? "Select all")}
                    </MenuItem>
                    <Divider />
                    {children}
                  </Paper>
                )
              : undefined
          }
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              label={config.label}
              fullWidth
            />
          )}
        />
      );
    }

    case "date-range": {
      const { startDate, endDate, onStartChange, onEndChange } = config;
      return (
        <Grid container spacing={2}>
          <Grid size={{ xs: 6 }}>
            <DatePicker
              label={config.labelFrom}
              onChange={(newValue) =>
                onStartChange(
                  newValue && newValue.isValid()
                    ? newValue.format("YYYY-MM-DD")
                    : "",
                )
              }
              slotProps={{
                textField: { variant: "outlined", fullWidth: true },
                actionBar: { actions: ["today", "clear"] },
              }}
              maxDate={endDate ? dayjs(endDate) : undefined}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <DatePicker
              label={config.labelTo}
              onChange={(newValue) =>
                onEndChange(
                  newValue && newValue.isValid()
                    ? newValue.format("YYYY-MM-DD")
                    : "",
                )
              }
              slotProps={{
                textField: { variant: "outlined", fullWidth: true },
                actionBar: { actions: ["today", "clear"] },
              }}
              minDate={startDate ? dayjs(startDate) : undefined}
            />
          </Grid>
        </Grid>
      );
    }

    default:
      return null;
  }
}
